import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { UserPen, X } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRoles } from 'features/role/hooks/useRoles';
import { useUpdateAdmin } from '../hooks/useUpdateAdmin';
import { editAdminSchema } from '../validations/adminEditForm';

export const EditAdminModal = (props) => {
  const { isEditModalOpen, setIsEditModalOpen, user } = props;
  const { mutate: updateUser, isPending } = useUpdateAdmin();
  const { data: rolesData } = useRoles({ limit: 99, isActive: true });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(editAdminSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      role: '',
      status: 'active',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        name: user.name || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        role: user.role?._id || '',
        status: user.status || 'active',
      });
    }
  }, [user, reset]);

  useEffect(() => {
    if (!isEditModalOpen) {
      reset();
    }
  }, [isEditModalOpen, reset]);

  const onSubmit = (data) => {
    const { phoneNumber, ...submitData } = data;
    const finalData = phoneNumber ? { ...submitData, phoneNumber } : submitData;
    const payload = { userId: user._id, userdata: finalData };

    updateUser(payload, {
      onSuccess: () => {
        reset();
        setIsEditModalOpen(false);
      },
    });
  };

  if (!isEditModalOpen || !user) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto'>
      <div className='bg-card rounded-lg shadow-xl w-full max-w-2xl border border-border my-8'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-border'>
          <h3 className='text-lg font-semibold text-foreground'>Edit User</h3>
          <button
            onClick={() => setIsEditModalOpen(false)}
            className='text-muted-foreground hover:text-foreground transition-colors'
            type='button'
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {/* Name */}
            <div>
              <label className='block text-sm font-medium text-foreground mb-1'>
                Name <span className='text-destructive'>*</span>
              </label>
              <input
                type='text'
                {...register('name')}
                className={`w-full px-3 py-2 ${errors.name ? 'border-destructive' : ''}`}
                placeholder='Enter admin name'
              />
              {errors.name && <p className='mt-1 text-xs text-destructive'>{errors.name.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label className='block text-sm font-medium text-foreground mb-1'>
                Email <span className='text-destructive'>*</span>
              </label>
              <input
                type='email'
                {...register('email')}
                className={`w-full px-3 py-2 ${errors.email ? 'border-destructive' : ''}`}
                placeholder='admin@example.com'
              />
              {errors.email && <p className='mt-1 text-xs text-destructive'>{errors.email.message}</p>}
            </div>

            {/* Phone Number */}
            <div>
              <label className='block text-sm font-medium text-foreground mb-1'>
                Phone Number <span className='text-muted-foreground text-xs'>(Optional)</span>
              </label>
              <input
                type='tel'
                {...register('phoneNumber')}
                className={`w-full px-3 py-2 ${errors.phoneNumber ? 'border-destructive' : ''}`}
                placeholder='1234567890'
              />
              {errors.phoneNumber && <p className='mt-1 text-xs text-destructive'>{errors.phoneNumber.message}</p>}
            </div>

            {/* Role */}
            <div>
              <label className='block text-sm font-medium text-foreground mb-1'>
                Role <span className='text-destructive'>*</span>
              </label>
              <select {...register('role')} className={`w-full px-3 py-2 ${errors.role ? 'border-destructive' : ''}`}>
                <option value=''>Select a role</option>
                {rolesData?.data?.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {errors.role && <p className='mt-1 text-xs text-destructive'>{errors.role.message}</p>}
            </div>

            {/* Status */}
            <div className='md:col-span-2'>
              <label className='block text-sm font-medium text-foreground mb-1'>
                Status <span className='text-destructive'>*</span>
              </label>
              <select
                {...register('status')}
                className={`w-full px-3 py-2 ${errors.status ? 'border-destructive' : ''}`}
              >
                <option value='active'>Active</option>
                <option value='inactive'>Inactive</option>
                <option value='suspended'>Suspended</option>
              </select>
              {errors.status && <p className='mt-1 text-xs text-destructive'>{errors.status.message}</p>}
            </div>
          </div>

          {/* Info Message */}
          <div className='mt-4 p-3 bg-primary/10 border border-primary/20 rounded-md'>
            <p className='text-xs text-primary'>
              Note: Password cannot be changed through this form. Use the password reset feature.
            </p>
          </div>

          {/* Buttons */}
          <div className='flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-6'>
            <button
              type='button'
              onClick={() => setIsEditModalOpen(false)}
              className='flex-1 px-4 py-2 border border-border rounded-md text-foreground hover:bg-accent transition-colors'
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={isPending}
            >
              {isPending ? (
                <div className='flex items-center justify-center'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2' />
                  Updating...
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <UserPen size={16} className='mr-2' />
                  Update User
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
